import type {
    OutreachType as UrlOutreachType,
    EmployerOutreachData,
} from './useEmployerOutreachSource';
import type { ResumeEngagement } from '../components/core/JobComponent/EmployerOutreach/ResumeEngagementDataBuilder';

import { type OutreachInfo, OutreachStatus, OutreachType } from './useEmployerOutreachUIConfig';

/**
 * Maps engagement status string to OutreachStatus enum
 */
const mapEngagementStatus = (status: string): OutreachStatus | null => {
    const upperStatus = status.toUpperCase();
    switch (upperStatus) {
        case 'SENT':
        case 'INITIATED':
            return OutreachStatus.INITIATED;
        case 'INTERESTED':
            return OutreachStatus.INTERESTED;
        case 'DECLINED':
            return OutreachStatus.DECLINED;
        default:
            return null;
    }
};

/**
 * Maps URL outreach type and engagement data to OutreachType enum
 * Uses the fullApplyRequested field from engagement to determine FAST_APPLY vs FULL_APPLY
 */
const mapOutreachType = (
    urlOutreachType: UrlOutreachType,
    fullApplyRequested: boolean,
): OutreachType | null => {
    if (!urlOutreachType) {
        return null;
    }

    // Use the fullApplyRequested field to determine the type
    // fullApplyRequested === false means FAST_APPLY
    // fullApplyRequested === true means FULL_APPLY
    if (fullApplyRequested) {
        return OutreachType.FULL_APPLY;
    } else {
        return OutreachType.FAST_APPLY;
    }
};

/**
 * Creates OutreachInfo from engagement data and URL params
 */
export const mapToOutreachInfo = (
    engagement: ResumeEngagement | null,
    employerOutreachData: EmployerOutreachData,
): OutreachInfo | null => {
    if (!engagement) {
        return null;
    }

    const outreachStatus = mapEngagementStatus(engagement.status);
    const outreachType = mapOutreachType(
        employerOutreachData.outreachType,
        engagement.fullApplyRequested,
    );

    if (!outreachStatus || !outreachType) {
        return null;
    }

    // Get employer name from job data or sender
    const employerName = engagement.job?.company || engagement.sender.name;

    return {
        outreachStatus,
        outreachType,
        employerName: employerName || undefined,
    };
};
